import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NgForm } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { take, takeUntil } from 'rxjs/operators';
import * as XLSX from 'xlsx-js-style';
import { ConfiguredToastrService } from '../../../../helper/toastr/configured-toastr.service';
import { DeactivationDialogService } from '../../../../helper/can-deactivate/deactivation-dialog.service';
import {
  ConfirmationDialogComponent
} from '../../../../helper/confirmation-dialog-component/confirmation-dialog.component';
import { EditColumnDialogComponent } from './edit-column-dialog/edit-column-dialog.component';
import { AnessaTranslationService } from '../../../../helper/translation/anessa-translation.service';
import { ProjectChecklistService } from '../checklist.service';
import {
  ChecklistCellDataItem,
  ProjectChecklist,
  ProjectChecklistColumn,
  ProjectChecklistRow
} from '../../../../models/project/project-info.model';
import Utils from '../../../../util';
import { EditRowDialogComponent } from './edit-row-dialog/edit-row-dialog.component';
import {
  ChecklistTemplateNameDialogComponent
} from '../checklist-template-name-dialog/checklist-template-name-dialog.component';
import { ProjectCommunicationService } from '../../../../communication/project-comms.service';
import { EditCellDialogComponent, EditCellDialogResponse } from './edit-cell-dialog/edit-cell-dialog.component';
import { v4 as uuid } from 'uuid';
import { ChecklistTemplateDialogComponent } from '../checklist-list/checklist-template-dialog/checklist-template-dialog.component';
import { CommunicationService } from '../../../../communication/communication.service';
import { UserpilotReloader } from '../../../../helper/userpilot-reloader.component';

@Component({
  selector: 'app-project-info-checklist-modify',
  templateUrl: './checklist-modify.component.html',
  styleUrls: [
    './checklist-modify.component.scss',
  ]
})
export class ProjectInfoChecklistModifyComponent extends UserpilotReloader implements OnInit, OnDestroy {
  @Input() isEmbedded = false;
  @Input() showTitle = true;
  @Input() set checklistInput(listInput: ProjectChecklist) {
    this.checklist = listInput;
    this.maxLevelShown = this.getMaxLevel();
    this.prepareTableData();
  }
  @Input() showSaveBtn = false;
  @Output() saved = new EventEmitter<ProjectChecklist>();
  @Output() cancel = new EventEmitter<boolean>();

  onDestroyNotifier = new EventEmitter<any>();

  @ViewChild('checklistForm') checklistForm: NgForm;
  @ViewChild('fileInput') fileInput: any;

  checklist: ProjectChecklist;
  existingChecklistNames: string[];
  checklistTemplates: any[];
  dataSource = new MatTableDataSource<ProjectChecklistRow>();
  editMode = false;

  hexCodeMap = {
    '#FF0000': 'red',
    '#FF681F': 'orange',
    '#00FF00': 'green',
    '#0000FF': 'blue',
    '#FFFF00': 'yellow',
  };

  displayedColumns: string[] = [];
  displayedControls: string[] = [];
  columnNames: string[] = [];
  controlNames: string[] = [];

  editIndex: number;
  editColumn: string;
  editRow: number;
  maxLevelShown: number = -1;

  pageDirty = false;

  showTable = false;
  showClearName = false;

  trigger: string;
  isProjectInput = false;
  isScopingResults = false;
  isInitialUser = false;
  readOnlyInitialChecklists: boolean;
  editOnlyInitialChecklists: boolean;

  constructor(
    private dialog: MatDialog,
    private http: HttpClient,
    private route: ActivatedRoute,
    private router: Router,
    private checklistService: ProjectChecklistService,
    private deactivationDialogService: DeactivationDialogService,
    private toastrService: ConfiguredToastrService,
    private translationService: AnessaTranslationService,
    private projectCommService: ProjectCommunicationService,
    private commService: CommunicationService
  ) {
    super();
  }

  ngOnInit() {
    this.isInitialUser = Utils.isInitialFormUser();
    if (!this.isEmbedded) {
      if (this.router.url.includes('project-input')) {
        this.isProjectInput = true;
      }
      if (this.route.snapshot.paramMap.has('index') && !this.isScopingResults) {
        this.checklistService.checklistsLoadedNotifier
          .pipe(takeUntil(this.onDestroyNotifier.asObservable()))
          .subscribe((loaded) => {
            if (loaded) {
              this.setup();
            }
          });
      } else {
        this.setup();
      }
    }
    this.translationService.languageChangeNotifier
      .pipe(takeUntil(this.onDestroyNotifier.asObservable()))
      .subscribe(() => {
        this.trigger = this.translationService.getCurrentLang();
      });
    // load template names and ids when available
    this.checklistService.checklistTemplatesLoadedNotifier.pipe(takeUntil(this.onDestroyNotifier))
      .subscribe((loaded) => {
        if (loaded) {
          this.checklistTemplates = this.checklistService.getChecklistTemplates();
        }
      });

    this.projectCommService.projectInputDraftSaveNotifier
    .pipe(takeUntil(this.onDestroyNotifier.asObservable()))
    .subscribe(save => {
      if (save === true && this.checklistForm) {
        try {
          if (this.checklistForm.valid) {
            this.onSave();
          } else {
            this.toastrService.error(this.translationService.get('PROJECT.PROJECT_INFO.CHECKLIST.TEMPLATE.ERROR_EMPTY_NAME'));
            this.projectCommService.projectInputDraftSaveNotifier.next(false);
          }
        } catch (error) {
          this.toastrService.error(error.message);
          this.projectCommService.projectInputDraftSaveNotifier.next(false);
        }
      }
    });

  }

  ngOnDestroy() {
    this.onDestroyNotifier.emit(true);
    this.onDestroyNotifier.unsubscribe();
  }

  private setup() {
    this.readOnlyInitialChecklists = Utils.readOnlyInitialChecklists();
    this.editOnlyInitialChecklists = Utils.editOnlyInitialChecklists();
    if (this.route.snapshot.paramMap.has('index')) {
      this.editIndex = Number(this.route.snapshot.paramMap.get('index'));
      this.checklist = this.checklistService.getChecklist(this.editIndex);
      this.existingChecklistNames = this.checklistService.getChecklistNames();
      if (!this.checklist) {
        this.onCancel();
        return;
      }
    } else {
      this.editIndex = -1;
      this.checklist = new ProjectChecklist();
      this.checklist.created = new Date();
      // Default columns
      this.checklist.columns = this.getDefaultColumns();
    }
    this.maxLevelShown = this.getMaxLevel();
    this.prepareTableData();
  }

  hideSaveButton() {
    if (this.showSaveBtn === true) return false;
    // return Utils.hasInputListAccess() && !Utils.hasFormAccess();
  }

  hideSidebarButton() {
    if (this.showSaveBtn === true) return false;
    return Utils.hasInputListAccess() && !Utils.hasFormAccess();
  }

  /**
   * clears the input that is using the provided property, assumes it is
   * part of the availability variable
   * @param property: the availability variable property name that is to be cleared
   */
  clearInput(property: string) {
    const newChecklsit = new ProjectChecklist();
    this.checklist[property] = newChecklsit[property];
    this.checklistForm.form.markAsDirty();
  }

  markFormAsDirty() {
    this.pageDirty = true;
    this.checklistService.checklistsModifiedNotifier.next(true)
  }

  canDeactivate() {
    if (this.pageDirty ||  (this.checklist && this.checklistForm.dirty)) {
      return this.deactivationDialogService.confirm(this.translationService.get('PROJECT.COMMON.DEACTIVATE_CONFIRM'));
    }
    return true;
  }

  /**
   * Recursive depth-first search to extract nested children to a single level list
   * @param rows list of rows to extract children from
   * @param parentIndex index of parent used to create dot id from
   * @returns a single level list containing all rows
   */
  private flattenChecklistRows(rows: ProjectChecklistRow[], parentIndex?: string) {
    const flattenedRows: ProjectChecklistRow[] = [];
    rows.forEach((row, index) => {
      let dotIndex: string;
      if (parentIndex) {
        dotIndex = `${parentIndex}.${String(index + 1)}`;
      } else {
        dotIndex = String(index + 1);
      }
      row.data.id = dotIndex;
      flattenedRows.push(row);
      // Only include rows up to the overall selected level
      if (row.level < this.maxLevelShown) {
        flattenedRows.push(...this.flattenChecklistRows(row.children, dotIndex));
      }
    });
    return flattenedRows;
  }

  private prepareTableData() {
    this.showTable = false;
    // Update max shown level if the max level is lower than the selected level
    if (this.maxLevelShown > this.getMaxLevel()) {
      this.maxLevelShown = this.getMaxLevel();
    }
    this.columnNames = this.checklist.columns.map(column => column.name);
    this.controlNames = this.columnNames.map(name => `control${name}`);
    this.displayedColumns = ['addControl', ...this.columnNames, 'removeControl'];
    this.displayedControls = ['intEmptyAdd', ...this.controlNames, 'intEmptyRemove'];
    if (this.checklist.rows.length > 0) {
      // Only show level control and id columns if there is at least one row
      if (this.editMode) {
        this.displayedColumns.splice(1, 0, 'levelControl', 'id');
      } else {
        this.displayedColumns.splice(1, 0, 'id');
      }
      this.displayedControls.splice(1, 0, 'intEmptyLevel', 'intEmptyId');
      const tableData = this.flattenChecklistRows(this.checklist.rows);
      this.dataSource.data = tableData;
    } else {
      this.dataSource.data = [];
    }
    this.showTable = true;
  }

  edit(row: number, col: string) {
    // check if row is locked before enabling the edit
    if (col && this.checklist.getColumnByName(col).locked || row && this.flattenChecklistRows(this.checklist.rows)[row].locked || !this.hasEditAccess()) {
      return;
    }
    this.editRow = row;
    this.editColumn = col;
  }

  isEdit(row: number, col: string) {
    if (col === 'id') {
      return false;
    }
    return this.editRow === row && this.editColumn === col;
  }

  /**
   * Determines if a string is a hex code
   * @param value string value to check if hex code
   * @returns true or false depending on if the string contains a hex code
   */
  isColourHexCode(value: string) {
    return value != null && String(value).startsWith('#');
  }

  getRowStyle(row: ProjectChecklistRow, colIndex) {
    // Use gradients of anessa blue
    if (row.level === 1 || colIndex === 0 || colIndex === undefined) {
      return { 'background-color': `rgba(142, 196, 249, ${0.6 / row.level})` };
    }
  }

  onCellOptionsClick(row: ProjectChecklistRow, colIndex: number) {
    if (!this.checklist || !this.checklist.columns || this.checklist.columns.length < 1) {
      return;
    }
    const dialogRef = this.dialog.open(EditCellDialogComponent, {
      width: '650px',
      data: {
        cellData: ChecklistCellDataItem.deepCopy(this.checklist.columns[colIndex]?.cellDropdownItems[row.uuid]),
        placeholder: row.dataPlaceholder[this.checklist.columns[colIndex].name],
        iconData: row.data[this.checklist.columns[colIndex].name],
        isIcon: this.checklist.columns[colIndex].isIcon
      },
      disableClose: true,
    });

    dialogRef.afterClosed()
      .pipe(take(1))
      .subscribe((data: EditCellDialogResponse) => {
        if (data) {
          if (data.updatedCellData != null) {
            if (this.checklist && this.checklist.columns && this.checklist.columns.length > 0) {
              if (this.checklist.columns[colIndex] && this.checklist.columns[colIndex].cellDropdownItems) {
                this.checklist.columns[colIndex].cellDropdownItems[row.uuid] = data.updatedCellData;
              }
            }
          }
          row.dataPlaceholder[this.checklist.columns[colIndex].name] = data.placeholder;
          if (this.checklist.columns[colIndex].isIcon) {
            row.data[this.checklist.columns[colIndex].name] = data.iconData;
          }
          this.pageDirty = true;
          this.prepareTableData();
        }
      }
    );
  }

  hasCellOptions(rowUuid: string, colIndex: number) {
    const foundItems = this.getCellOptions(rowUuid, colIndex);
    return foundItems && foundItems.length > 0;
  }

  getCellOptions(rowUuid: string, colIndex: number): string[] {
    if (this.checklist && this.checklist.columns && this.checklist.columns[colIndex]) {
      const targetCol = this.checklist.columns[colIndex];
      if (targetCol && targetCol.cellDropdownItems && targetCol.cellDropdownItems[rowUuid]) {
        return targetCol.cellDropdownItems[rowUuid].items;
      }
    }
    return null;
  }

  /**
   * Recursive function to find the parent row containing the given row
   * @param rows list of rows to search for given row in
   * @param orphan row to find parent list
   * @returns true if in top row list, parent row if found, or null if given row is truely orphaned
   */
  private findParentRow(rows: ProjectChecklistRow[], orphan: ProjectChecklistRow): ProjectChecklistRow | boolean {
    if (rows.includes(orphan)) {
      return true;
    } else {
      for (const row of rows) {
        const parentFound = this.findParentRow(row.children, orphan);
        if (parentFound != null) {
          if (parentFound === true) {
            // Return parent row
            return row;
          } else {
            // If we already found the parent, continue passing it up through the recursive calls
            return parentFound;
          }
        }
      }
    }
    return null;
  }

  getMaxLevel() {
    let maxLevel = 0;
    const recFunc = (rows: ProjectChecklistRow[]) => {
      for (const row of rows) {
        if (row.level > maxLevel) {
          maxLevel = row.level;
        }
        if (row.children.length > 0) {
          recFunc(row.children);
        }
      }
    };
    recFunc(this.checklist.rows);
    return maxLevel;
  }

  getLevels(level: number, row?: ProjectChecklistRow, includeAll = true) {
    const levels = [];
    if (includeAll) {
      for (let i = level; i > 0; i--) {
        levels.push(i);
      }
    } else {
      // Add one level deeper if not first item
      const parentRow = this.findParentRow(this.checklist.rows, row);
      if (parentRow == null) {
        return [];
      }
      let index: number;
      if (parentRow === true) {
        index = this.checklist.rows.indexOf(row);
      } else {
        const children = (parentRow as ProjectChecklistRow).children;
        index = children.indexOf(row);
      }
      if (index > 0) {
        levels.push(level + 1);
      }
      levels.push(level);
      // Add one level higher if not in top row
      if (level > 1) {
        levels.push(level - 1);
      }
    }
    return levels.reverse();
  }

  onMaxLevelChange() {
    this.prepareTableData();
  }

  /**
   * Update the level of all nested children of the given row
   * @param row row to update level of children
   * @param changeFactor a positive or negative value to add to the level (e.g. 1, -1)
   */
  private updateChildLevels(row: ProjectChecklistRow, changeFactor: number) {
    for (const childRow of row.children) {
      childRow.level += changeFactor;
      this.updateChildLevels(childRow, changeFactor);
    }
  }

  /**
   * Moves a row up or down a level including any child rows
   * @param index index of the row to move
   * @param newLevel level to move the row to
   */
  onLevelChange(index: number, newLevel: number) {
    const currentRow = this.flattenChecklistRows(this.checklist.rows)[index];

    if (newLevel > currentRow.level) {
      // Move row down a level
      const parentRow = this.findParentRow(this.checklist.rows, currentRow);
      let newParent: ProjectChecklistRow;
      if (parentRow === true) {
        const parentIndex = this.checklist.rows.indexOf(currentRow);
        newParent = this.checklist.rows[parentIndex - 1];
        // Remove from current parent
        this.checklist.rows.splice(parentIndex, 1);
      } else {
        const children = (parentRow as ProjectChecklistRow).children;
        const parentIndex = children.indexOf(currentRow);
        newParent = children[parentIndex - 1];
        // Remove from current parent
        children.splice(parentIndex, 1);
      }
      // Add to new parent
      currentRow.level = newLevel;
      this.updateChildLevels(currentRow, 1);
      newParent.children.push(currentRow);
    } else {
      // Move row up a level
      const parentRow = this.findParentRow(this.checklist.rows, currentRow) as ProjectChecklistRow;
      const grandparentRow = this.findParentRow(this.checklist.rows, parentRow);
      // Add to grandparent
      if (grandparentRow === true) {
        const grandparentIndex = this.checklist.rows.indexOf(parentRow);
        this.checklist.rows.splice(grandparentIndex + 1, 0, currentRow);
      } else {
        const children = (grandparentRow as ProjectChecklistRow).children;
        const grandparentIndex = children.indexOf(parentRow);
        children.splice(grandparentIndex + 1, 0, currentRow);
      }
      currentRow.level = newLevel;
      this.updateChildLevels(currentRow, -1);
      // Remove from parent
      const parentIndex = parentRow.children.indexOf(currentRow);
      parentRow.children.splice(parentIndex, 1);
    }
    // Update max shown level if this row would be hidden after level change
    if (currentRow.level > this.maxLevelShown) {
      this.maxLevelShown = this.getMaxLevel();
    }
    this.pageDirty = true;
    this.prepareTableData();
  }

  onAddColumn(index: number) {
    const dialogRef = this.dialog.open(EditColumnDialogComponent, {
      width: '650px',
      data: {
        column: null,
        names: this.checklist.columns.map(column => Utils.stringToNameObject(column.name)),
      },
      disableClose: true,
    });

    dialogRef.afterClosed().toPromise().then(
      (column: ProjectChecklistColumn) => {
        if (column != null) {
          this.checklist.columns.splice(index + 1, 0, column);
          this.flattenChecklistRows(this.checklist.rows).forEach(row => {
            row.data[column.name] = null;
          });
          this.pageDirty = true;
          this.prepareTableData();
        }
      }
    );
  }

  onEditColumn(index: number) {
    const dialogRef = this.dialog.open(EditColumnDialogComponent, {
      width: '650px',
      data: {
        column: ProjectChecklistColumn.deepCopy(this.checklist.columns[index]),
        names: this.checklist.columns.filter(column => column.name !== this.checklist.columns[index].name).map(column => Utils.stringToNameObject(column.name)),
      },
      disableClose: true,
    });

    dialogRef.afterClosed().toPromise().then(
      (column: ProjectChecklistColumn) => {
        if (column != null) {
          const originalName = this.checklist.columns[index].name;
          this.checklist.columns.splice(index, 1, column);
          if (originalName !== column.name) {
            this.flattenChecklistRows(this.checklist.rows).forEach(row => {
              row.data[column.name] = row.data[originalName];
              delete row.data[originalName];
              row.dataPlaceholder[column.name] = row.dataPlaceholder[originalName];
              delete row.dataPlaceholder[originalName];
            });
          }
          this.pageDirty = true;
          this.prepareTableData();
        }
      }
    );
  }

  onRemoveColumn(index: number) {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        message: this.translationService.get('PROJECT.PROJECT_INFO.CHECKLIST.REMOVE_COLUMN_WARNING'),
      }
    });

    dialogRef.afterClosed().toPromise().then((confirm: boolean) => {
      if (confirm) {
        const [columnName] = this.checklist.columns.splice(index, 1);
        this.flattenChecklistRows(this.checklist.rows).forEach(row => {
          delete row.data[columnName.name];
        });
        this.pageDirty = true;
        this.prepareTableData();
      }
    });
  }

  onAddRow(index: number) {
    const newRow = new ProjectChecklistRow();
    this.checklist.columns.forEach(column => {
      newRow.data[column.name] = null;
    });

    // Special case for adding row at top
    if (index < 0) {
      this.checklist.rows.splice(0, 0, newRow);
      this.maxLevelShown = this.getMaxLevel();
      this.pageDirty = true;
      this.prepareTableData();
      return;
    }

    const currentRow = this.flattenChecklistRows(this.checklist.rows)[index];
    newRow.level = currentRow.level;

    const parentRow = this.findParentRow(this.checklist.rows, currentRow);
    if (parentRow === true) {
      this.checklist.rows.splice(this.checklist.rows.indexOf(currentRow) + 1, 0, newRow);
    } else {
      const children = (parentRow as ProjectChecklistRow).children;
      children.splice(children.indexOf(currentRow) + 1, 0, newRow);
    }

    this.pageDirty = true;
    this.prepareTableData();
  }

  onEditRow(index: number) {
    const dialogRef = this.dialog.open(EditRowDialogComponent, {
      width: '750px',
      data: {
        row: ProjectChecklistRow.deepCopy(this.flattenChecklistRows(this.checklist.rows)[index])
      },
      disableClose: true,
    });

    dialogRef.afterClosed().toPromise().then(
      (row: ProjectChecklistRow) => {
        if (row != null) {
          const currentRow = this.flattenChecklistRows(this.checklist.rows)[index];
          const parentRow = this.findParentRow(this.checklist.rows, currentRow);
          if (parentRow === true) {
            this.checklist.rows[this.checklist.rows.indexOf(currentRow)] = row;
          } else if (parentRow !== null) {
            const children = (parentRow as ProjectChecklistRow).children;
            children[children.indexOf(currentRow)] = row;
          }
          this.prepareTableData();
          this.pageDirty = true;
        }
      }
    );
  }

  private removeRow(row: ProjectChecklistRow) {
    const parentRow = this.findParentRow(this.checklist.rows, row);
    if (parentRow === true) {
      this.checklist.rows.splice(this.checklist.rows.indexOf(row), 1);
    } else {
      const children = (parentRow as ProjectChecklistRow).children;
      children.splice(children.indexOf(row), 1);
    }

    this.pageDirty = true;
    this.prepareTableData();
  }

  onRemoveRow(index: number) {
    const currentRow = this.flattenChecklistRows(this.checklist.rows)[index];
    let message: string;
    if (currentRow.children.length > 0) {
      message = this.translationService.get('PROJECT.PROJECT_INFO.CHECKLIST.REMOVE_CHILDREN_WARNING');
    } else {
      message = this.translationService.get('PROJECT.PROJECT_INFO.CHECKLIST.REMOVE_ROW_WARNING');
    }
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        message: message,
      }
    });

    dialogRef.afterClosed().toPromise().then((confirm: boolean) => {
      if (confirm) {
        this.removeRow(currentRow);
      }
    });
  }

  onSave() {
    this.pageDirty = false;
    this.checklistForm.form.markAsPristine();
    this.checklist.lastModified = new Date();
    if (this.isEmbedded) {
      this.saved.emit(this.checklist);
    } else {
      if (this.editIndex > -1) {
        this.checklistService.editChecklist(this.editIndex, this.checklist);
        this.router.navigate(['../..'], { relativeTo: this.route });
      } else {
        this.checklistService.addChecklist(this.checklist);
        this.router.navigate(['..'], { relativeTo: this.route });
      }
    }
  }

  onCancel() {
    this.pageDirty = false;
    if (this.checklistForm?.form) {
      this.checklistForm.form.markAsPristine();
    }
    if (this.isEmbedded) {
      this.cancel.emit(true);
    } else {
      if (this.editIndex > -1) {
        this.router.navigate(['../..'], { relativeTo: this.route });
      } else {
        this.router.navigate(['..'], { relativeTo: this.route });
      }
    }
  }

  private getTemplateNameList() {
    const names = [];
    for (const template of this.checklistTemplates) {
      names.push(template.name);
    }
    return names;
  }

  async onLoadFromTemplateClick() {
    const response: { templateID: string, isTemplateDelete: boolean } = await this.dialog
      .open(ChecklistTemplateDialogComponent, {
        width: '1000px',
        data: {
          templates: this.checklistTemplates
        }
      }).afterClosed().toPromise();
    if (!response) return;
    const templateID = response.templateID;
    const isTemplateDelete = response.isTemplateDelete;


    if (!isTemplateDelete) {
      // template load
      for (const template of this.checklistTemplates) {
        if (template.templateid === templateID) {
          break;
        }
      }
      await this.fetchAndLoadTemplate(templateID);
    } else { // delete template
      this.onTemplateDelete(templateID);
    }
  }

  async fetchAndLoadTemplate(templateID: string) {
    if (templateID) {
      // ask if they are sure.
      const proceed = await this.dialog.open(ConfirmationDialogComponent, {
        data: {
          message: this.translationService.get('PROJECT.PROJECT_INFO.CHECKLIST.IMPORT_DATA_WARNING'),
        }
      }).afterClosed().toPromise();

      if (proceed) {
        const loadedTemplate = await this.checklistService.loadTemplate(templateID);
        if (loadedTemplate) {
          this.checklist.columns = loadedTemplate.columns;
          this.checklist.rows = loadedTemplate.rows;
          this.maxLevelShown = this.getMaxLevel();
          this.pageDirty = true;
          this.prepareTableData();
        }
      }
    }
  }

  onSaveAsTemplate() {
    const nameDialog = this.dialog.open(ChecklistTemplateNameDialogComponent, {
      width: '400px',
      data: {
        name: name,
        forbiddenNames: this.getTemplateNameList()
      }
    });
    nameDialog.afterClosed().pipe(take(1)).subscribe(
      (response: string) => {
        console.log(response);
        if (!response) {
          return;
        }
        const template = Utils.deepCopy(this.checklist);
        template.name = response;
        this.commService.postRequest({ checklist: template }, '/wsasapi/checklist/saveTemplate')
          .pipe(take(1))
          .pipe(takeUntil(this.onDestroyNotifier))
          .subscribe(result => {
            if (result) {
              // append new template to exising ones so we don't have to reload them
              const templateid = result['obj']['_id'];
              this.checklistTemplates.push({ name: result['obj']['checklist']['name'], templateid: templateid });
              this.toastrService.success(this.translationService.get(
                "PROJECT.PROJECT_INFO.CHECKLIST.TEMPLATE.SAVE_TEMPLATE_SUCCESS"
              ));
            }
          });
      }
    );
  }

  onTemplateDelete(templateID: string) {
    const templateName = this.checklistTemplates.find(element => element.templateid === templateID).name;
    const confirmDialog = this.dialog.open(ConfirmationDialogComponent, {
      data: { message: this.translationService.get('PROJECT.PROJECT_INFO.CHECKLIST.TEMPLATE.DELETE_TEMPLATE_CONFIRMATION') + ' ' + templateName}
    });

    confirmDialog.afterClosed().pipe(take(1)).pipe(takeUntil(this.onDestroyNotifier))
      .subscribe((result) => {
        // handle cancel
        if (!result) {
          return;
        }

        this.commService.deleteRequest(templateID, '/wsasapi/checklist/delete').pipe(take(1)).pipe(takeUntil(this.onDestroyNotifier))
          .subscribe(
            (response) => {
              console.log(response);
              this.checklistTemplates = this.checklistTemplates.filter(item => item.templateid !== templateID);
              this.toastrService.success(this.translationService.get('PROJECT.PROJECT_INFO.CHECKLIST.TEMPLATE.DELETE_TEMPLATE_SUCCESS'));
            }
          );
      });


  }

  /**
   * Parses an XLSX file into checklist formatted data using the xlsx npm package
   * Documentation: https://docs.sheetjs.com/
   *
   * @param target HTML input element containing the uploaded file
   */
  async importData(target: HTMLInputElement) {

    const proceed = await this.dialog.open(ConfirmationDialogComponent, {
      data: {
        message: this.translationService.get('PROJECT.PROJECT_INFO.CHECKLIST.IMPORT_DATA_WARNING'),
      }
    }).afterClosed().toPromise();

    const files = target.files;
    if (proceed && files && files.length) {
      const fileToRead = files[0];

      const fileParts = fileToRead.name.split('.');
      if (String(fileParts[fileParts.length - 1]).toLowerCase() !== 'xlsx') {
        this.toastrService.error(this.translationService.get('PROJECT.PROJECT_INFO.CHECKLIST.ONLY_XLSX_WARNING'));
        return;
      }

      const fileReader = new FileReader();
      fileReader.onload = (e) => {
        const data = new Uint8Array(e.target.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        if (!workbook.SheetNames.includes('template')) {
          throw Error(this.translationService.get('PROJECT.PROJECT_INFO.CHECKLIST.INVALID_XLSX'));
        }
        const worksheet = workbook.Sheets['template'];
        const checklist = this.worksheetToChecklist(worksheet);
        this.checklist.columns = checklist.columns;
        this.checklist.rows = checklist.rows;
        this.maxLevelShown = this.getMaxLevel();
        this.pageDirty = true;
        this.prepareTableData();
      };
      fileReader.readAsArrayBuffer(fileToRead);
    }
    // Reset input
    this.fileInput.nativeElement.value = '';
  }

  /**
   * Searches a worksheet for a cell containing the given value
   * @param worksheet worksheet to search for the cell in
   * @param value value to find the cell containing
   * @returns a list of references to the cells containing the given value
   */
  private getCellData(worksheet: XLSX.WorkSheet, value: string): { key: string, start: string, end: string }[] {
    const cellData: { key: string, start: string, end: string }[] = [];

    // item[0] is the key, item[1] is the value
    const filteredData = Object.entries(worksheet).filter(
      item => item[1]['t'] === 's' && item[1]['v'].toLowerCase() === value.toLowerCase()
    );
    for (const filteredItem of filteredData) {
      cellData.push({ key: filteredItem[0], start: null, end: null });
    }

    if (cellData.length === 0) {
      return [];
    }

    // Set start and end columns if cell is a merge
    for (const foundCell of cellData) {
      // TODO: This will likely have problems if columns AA and up are used. This only expects 1 letter
      const c = foundCell.key.charCodeAt(0) - 'A'.charCodeAt(0);
      const r = Number(foundCell.key.slice(1)) - 1;
      const columnRange = worksheet['!merges'].find(merge => merge.s.c === c && merge.s.r === r);
      if (columnRange) {
        foundCell.start = String.fromCharCode('A'.charCodeAt(0) + columnRange.s.c);
        foundCell.end = String.fromCharCode('A'.charCodeAt(0) + columnRange.e.c);
      }
    }

    return cellData;
  }

  private getNthIndexOf(str, subStr, index) {
    return str.split(subStr, index).join(subStr).length;
  }

  /**
   * Creates a Checklist object from XLSX worksheet data
   * @param worksheet SheetJS representation of XLSX worksheet data
   * @returns a Checklist object created from the parsed XLSX data
   */
  private worksheetToChecklist(worksheet: XLSX.WorkSheet): ProjectChecklist {
    const checklist = new ProjectChecklist();

    // e.g. { key: C2, start: C, end: K }
    const mainTableCell = this.getCellData(worksheet, 'main table')[0];
    const validationLevelsCell = this.getCellData(worksheet, 'validation levels')[0];

    // Get max row
    let maxRow: number;
    for (let j = 4; ; j++) {
      const rowCellKey = `B${j}`;
      if (rowCellKey in worksheet) {
        maxRow = j;
      } else {
        break;
      }
    }

    const columns: ProjectChecklistColumn[] = [];
    // Get column names
    for (let i = mainTableCell.start.charCodeAt(0); i <= mainTableCell.end.charCodeAt(0); i++) {
      const columnNameKey = `${String.fromCharCode(i)}${3}`;
      columns.push(new ProjectChecklistColumn({
        uuid: uuid(), name: worksheet[columnNameKey].v, acceptedItems: [], cellDropdownItems: [], locked: false
      }));
      if (columnNameKey in worksheet) {
      }
    }

    const rows: ProjectChecklistRow[] = [];
    // Get row data
    for (let j = 4; j <= maxRow; j++) {
      const row = new ProjectChecklistRow();
      const rowLevelCellKey = `B${j}`;
      row.level = worksheet[rowLevelCellKey].v;
      for (let i = mainTableCell.start.charCodeAt(0); i <= mainTableCell.end.charCodeAt(0); i++) {
        const columnNameKey = `${String.fromCharCode(i)}${3}`;
        if (!(columnNameKey in worksheet)) {
          continue;
        }
        const columnName = worksheet[columnNameKey].v;
        const rowCellKey = `${String.fromCharCode(i)}${j}`;
        if (rowCellKey in worksheet) {
          // Convert all values to strings when importing
          row.data[columnName] = String(worksheet[rowCellKey].v);
        }
      }
      rows.push(row);
    }

    const treeRows: ProjectChecklistRow[] = [];
    // Organize rows into tree structure
    for (const row of rows) {
      if (!('Id' in row.data)) {
        throw Error(this.translationService.get('PROJECT.PROJECT_INFO.CHECKLIST.MISSING_ID_COLUMN'));
      }
      if (row.level === 1) {
        treeRows.push(row);
      } else {
        const index = this.getNthIndexOf(row.data['Id'], '.', row.level - 1);
        const parentRow = rows.find(tRow => String(tRow.data['Id']) === String(row.data['Id']).slice(0, index));
        if (!parentRow) {
          throw Error(this.translationService.get('PROJECT.PROJECT_INFO.CHECKLIST.INVALID_XLSX'));
        }
        parentRow.children.push(row);
      }
    }
    // Remove Id column
    rows.forEach(row => {
      delete row.data['Id'];
    });
    columns.splice(columns.findIndex(column => column.name === 'Id'), 1);

    // Get accepted items
    if (validationLevelsCell) {
      for (let i = validationLevelsCell.start.charCodeAt(0); i <= validationLevelsCell.end.charCodeAt(0); i++) {
        const columnNameKey = `${String.fromCharCode(i)}${3}`;
        if (columnNameKey in worksheet) {
          const column = columns.find(column => column.name === worksheet[columnNameKey].v);
          if (column) {
            // Loop until all items are found
            for (let j = 4; ; j++) {
              const rowCellKey = `${String.fromCharCode(i)}${j}`;
              if (rowCellKey in worksheet) {
                column.acceptedItems.push(String(worksheet[rowCellKey].v));
              } else {
                break;
              }
            }
            // Remove any empty strings
            column.acceptedItems = column.acceptedItems.filter(item => item !== '');
          }
        }
      }
    }

    // Identify any validation levels that need to be converted to colours
    for (const column of columns) {
      for (const item of column.acceptedItems) {
        if (item.match(/\w+ circle/)) {
          const [colour] = item.split(' ');
          const hexCode = Object.keys(this.hexCodeMap).find(key => this.hexCodeMap[key] === colour);
          if (hexCode) {
            column.acceptedItems.splice(column.acceptedItems.indexOf(item), 1, hexCode);
            for (const row of rows) {
              if (row.data[column.name] === item) {
                row.data[column.name] = hexCode;
              }
            }
          }
        }
      }
    }

    checklist.columns = columns;
    checklist.rows = treeRows;

    return checklist;
  }

  /**
   * Apply a given style to all cells in a column between the specified start
   * and end rows
   * @param worksheet worksheet to apply styling to
   * @param column column letter to apply styling to
   * @param row first row to apply cell styling to
   * @param maxRow last row to apply cell styling to
   * @param style style to apply to the cell(s)
   */
  private applyStyleToColumn(worksheet: XLSX.WorkSheet, column: string, row: number, maxRow: number, style: any) {
    for (let i = row; i <= maxRow; i++) {
      const rowCellKey = `${column}${i}`;
      if (rowCellKey in worksheet) {
        worksheet[rowCellKey].s = JSON.parse(JSON.stringify(style));
      } else {
        worksheet[rowCellKey] = { v: '', t: 's', s: JSON.parse(JSON.stringify(style)) };
      }
    }
  }

  /**
   * Make the exported XLSX worksheet pretty
   * @param worksheet reference to the worksheet to stylize
   */
  private styleWorksheet(worksheet: XLSX.WorkSheet, checklist: ProjectChecklist) {
    const getCellRef = (value: string) => {
      const cellDatas = this.getCellData(worksheet, value);
      if (cellDatas.length > 0) {
        return worksheet[cellDatas[0].key];
      } else {
        return null;
      }
    };

    // Get max row
    let maxRow: number;
    for (let j = 4; ; j++) {
      const rowCellKey = `B${j}`;
      if (rowCellKey in worksheet) {
        maxRow = j;
      } else {
        break;
      }
    }

    // Headers
    const mainTableCell = getCellRef('main table');
    mainTableCell.s = {
      alignment: {
        horizontal: 'center',
      },
    };
    const validationLevelsCell = getCellRef('validation levels');
    if (validationLevelsCell) {
      validationLevelsCell.s = {
        alignment: {
          horizontal: 'center',
        },
      };
    }

    // Level column
    const lvlCellRef = this.getCellData(worksheet, 'lvl')[0];
    const lvlCellStyle = {
      alignment: {
        horizontal: 'center',
      },
      border: {
        top: { style: 'thin', color: { rgb: 'a6a6a6' } },
        bottom: { style: 'thin', color: { rgb: 'a6a6a6' } },
        left: { style: 'thin', color: { rgb: 'a6a6a6' } },
        right: { style: 'thin', color: { rgb: 'a6a6a6' } },
      },
    };
    this.applyStyleToColumn(worksheet, lvlCellRef.key[0], 3, maxRow, lvlCellStyle);

    // Id column
    const idCellRef = this.getCellData(worksheet, 'Id')[0];
    const idCellStyle = {
      alignment: {
        horizontal: 'right',
      },
      border: {
        bottom: { style: 'dashed', color: { rgb: 'a6a6a6' } },
      },
      font: {
        name: 'Calibri',
        sz: '11',
      },
    };
    this.applyStyleToColumn(worksheet, idCellRef.key[0], 3, maxRow, idCellStyle);
    const idHeaderCell = worksheet[`${idCellRef.key[0]}3`];
    if (idHeaderCell && idHeaderCell.s) {
      if (idHeaderCell.s['font']) {
        idHeaderCell.s['font']['bold'] = true;
        idHeaderCell.s['font']['color'] = JSON.parse(JSON.stringify({ rgb: 'FFFFFF' }));
      }
      if (idHeaderCell.s['fill']) {
        idHeaderCell.s['fill'] = JSON.parse(JSON.stringify({ fgColor: { rgb: '1F4E78' } }));
      }
    }

    // Data columns
    const columnCellStyle = {
      alignment: {
        horizontal: 'center',
      },
      border: {
        bottom: {},
      },
      font: {
        name: 'Calibri',
        sz: '11',
      },
    };
    const maxValidationLevelItemCount = checklist.columns.reduce((max, col) => (col.acceptedItems.length > max.acceptedItems.length) ? col : max).acceptedItems.length;
    for (const column of checklist.columns) {
      columnCellStyle.alignment.horizontal = (column.acceptedItems.length > 0) ? 'center' : 'left';

      const cellRefs = this.getCellData(worksheet, column.name);
      cellRefs.forEach((cellRef, index) => {
        if (index > 0) {
          columnCellStyle.border.bottom = {};
          this.applyStyleToColumn(worksheet, cellRef.key[0], 3, 3 + maxValidationLevelItemCount, columnCellStyle);
        } else {
          columnCellStyle.border.bottom = { style: 'dashed', color: { rgb: 'a6a6a6' } };
          this.applyStyleToColumn(worksheet, cellRef.key[0], 3, maxRow, columnCellStyle);
        }
        const columnHeaderCell = worksheet[`${cellRef.key[0]}3`];
        if (idHeaderCell && idHeaderCell.s) {
          if (columnHeaderCell.s['font']) {
            columnHeaderCell.s['font']['bold'] = true;
            columnHeaderCell.s['font']['color'] = JSON.parse(JSON.stringify({ rgb: 'FFFFFF' }));
          }
          if (columnHeaderCell.s['fill']) {
            columnHeaderCell.s['fill'] = JSON.parse(JSON.stringify({ fgColor: { rgb: '1F4E78' } }));
          }
        }
      });
    }

    // Table borders
    const headerCellStyle = {
      alignment: {
        horizontal: 'center',
      },
      fill: {
        fgColor: { rgb: '000000' },
      },
      font: {
        bold: true,
        color: { rgb: 'FFFFFF' },
        name: 'Calibri',
        sz: '14',
      },
    };

    const mainTableCellRef = this.getCellData(worksheet, 'main table')[0];
    for (let i = mainTableCellRef.start.charCodeAt(0); i <= mainTableCellRef.end.charCodeAt(0); i++) {
      const columnNameKey = `${String.fromCharCode(i)}${2}`;
      if (columnNameKey in worksheet) {
        worksheet[columnNameKey].s = JSON.parse(JSON.stringify(headerCellStyle));
      } else {
        worksheet[columnNameKey] = { v: '', t: 's', s: JSON.parse(JSON.stringify(headerCellStyle)) };
      }
    }

    if (validationLevelsCell) {
      const validationLevelsCellRef = this.getCellData(worksheet, 'validation levels')[0];
      for (let i = validationLevelsCellRef.start.charCodeAt(0); i <= validationLevelsCellRef.end.charCodeAt(0); i++) {
        const columnNameKey = `${String.fromCharCode(i)}${2}`;
        if (columnNameKey in worksheet) {
          worksheet[columnNameKey].s = JSON.parse(JSON.stringify(headerCellStyle));
        } else {
          worksheet[columnNameKey] = { v: '', t: 's', s: JSON.parse(JSON.stringify(headerCellStyle)) };
        }
      }
    }

    // Row colours
    const getRowColour = function (row: ProjectChecklistRow) {
      const colours = ['bdd7ee', 'f2f2f2', 'ffffff'];
      if (row.level > colours.length) {
        return 'FFFFFF';  // white
      } else {
        return colours[row.level - 1];
      }
    };
    const flattenedRows = this.flattenChecklistRows(checklist.rows);
    for (let i = 4; i < flattenedRows.length + 4; i++) {
      for (let j = mainTableCellRef.start.charCodeAt(0); j <= mainTableCellRef.end.charCodeAt(0); j++) {
        const columnNameKey = `${String.fromCharCode(j)}${i}`;
        if (columnNameKey in worksheet) {
          worksheet[columnNameKey].s['fill'] = JSON.parse(JSON.stringify({ fgColor: { rgb: getRowColour(flattenedRows[i - 4]) } }));
        }
      }
    }
  }

  private getColumnWidths(data: string[][]): XLSX.ColInfo[] {
    const columnWidths: XLSX.ColInfo[] = [];
    for (const row of data) {
      row.forEach((column, index) => {
        if (columnWidths[index] == null) {
          columnWidths.splice(index, 0, { wch: String(column).length });
        } else {
          if (String(column).length > columnWidths[index].wch) {
            columnWidths[index].wch = String(column).length;
          }
        }
      });
    }
    columnWidths.forEach(width => {
      // Set the width of empty cells to 50px
      if (width.wch === 0) {
        width.wpx = 50;
      }
    });
    return columnWidths;
  }

  /**
   * Creates an XLSX workbook object from checklist data
   * @param checklist database representation of the checklist data
   * @returns a SheetJS representation of an XLSX workbook
   */
  private checklistToWorkbook(checklist: ProjectChecklist): XLSX.WorkBook {
    const columnNames = checklist.columns.map(column => column.name);
    const flattenedRows = this.flattenChecklistRows(checklist.rows);

    const workbook = XLSX.utils.book_new();

    workbook.Props = {
      Title: 'anessa checklist data',
      Author: 'anessa',
      CreatedDate: new Date(),
    };

    workbook.SheetNames.push('template');

    // Outer list is the rows, inner list is columns
    const worksheetData = [
      [],  // intentionally blank
      ['', '', 'Main table'], // headers
      ['', 'lvl', 'Id'],  // column names
    ];

    // Add columns
    for (const column of columnNames) {
      worksheetData[2].push(column);
      // Add empty cells to main table header
      worksheetData[1].push('');
    }
    // Add empty cell to separate main table and validation levels table
    worksheetData[1].push('');
    worksheetData[2].push('');

    // Add rows
    for (const row of flattenedRows) {
      const newRow = ['', row.level, row.data.id];
      for (const column of columnNames) {
        newRow.push(row.data[column]);
      }
      // Add empty cell to separate main table and validation levels table
      newRow.push('');
      worksheetData.push(newRow);
    }

    // Add validation levels if any present
    const validationLevelsPresent = checklist.columns.some(column => column.acceptedItems.length > 0);
    let columnsWithLevelsCount: number;
    if (validationLevelsPresent) {
      // This starts at -1 because it is used to calculate how many cells to merge
      // If only one column is present, no cells should be merged
      columnsWithLevelsCount = -1;
      worksheetData[1].push('validation levels');
      for (const column of checklist.columns) {
        if (column.acceptedItems.length > 0) {
          columnsWithLevelsCount++;
          worksheetData[2].push(column.name);
          const maxHeight = Math.max(flattenedRows.length, column.acceptedItems.length);
          for (let i = 0; i < maxHeight; i++) {
            // Add row if not present
            if ((3 + i) >= worksheetData.length) {
              worksheetData.push([]);
              let columnIndex: number;
              if (i > 0) {
                columnIndex = worksheetData[3 + i - 1].indexOf(column.acceptedItems[i - 1]);
              } else {
                columnIndex = 4 + columnNames.length;
              }
              // Add empty cells up to this column if necessary
              for (let j = 0; j < columnIndex; j++) {
                worksheetData[3 + i].push(null);
              }
            }
            worksheetData[3 + i].push(column.acceptedItems[i]);
          }
          // Add empty cells to validation levels header
          worksheetData[1].push(null);
        }
      }
    }

    // Replace hex codes with colour names
    for (const row of worksheetData) {
      for (const column of row) {
        if (this.isColourHexCode(column) && column in this.hexCodeMap) {
          row.splice(row.indexOf(column), 1, `${this.hexCodeMap[column]} circle`);
        }
      }
    }

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Set margins
    worksheet['!margins'] = {
      header: 0.3,
      footer: 0.3,
      left: 0.7,
      right: 0.7,
      top: 0.75,
      bottom: 0.75,
    };

    // Set column widths
    worksheet['!cols'] = this.getColumnWidths(worksheetData);

    worksheet['!merges'] = [];
    // Merge main table header
    const mainTableIndex = worksheetData[1].indexOf('Main table');
    worksheet['!merges'].push({ s: { c: mainTableIndex, r: 1 }, e: { c: mainTableIndex + columnNames.length, r: 1 } });
    // Merge validation items header
    if (validationLevelsPresent) {
      const validationLevelsIndex = worksheetData[1].indexOf('validation levels');
      worksheet['!merges'].push({ s: { c: validationLevelsIndex, r: 1 }, e: { c: validationLevelsIndex + columnsWithLevelsCount, r: 1 } });
    }

    // Set cell styling
    this.styleWorksheet(worksheet, checklist);

    workbook.Sheets['template'] = worksheet;

    return workbook;
  }

  /**
   * Exports checklist data into an XLSX formatted file using the xlsx npm package
   * Documentation: https://docs.sheetjs.com/
   */
  exportData() {
    const workbook = this.checklistToWorkbook(this.checklist);
    // Output file from workbook
    const name = (this.checklist.name) ? this.checklist.name : 'export';
    XLSX.writeFile(workbook, `${name}.xlsx`);
  }

  downloadTemplate() {
    this.http.get('assets/templates/checklist.xlsx', { responseType: 'blob' }).subscribe(result => {
      const url = window.URL.createObjectURL(result);
      const link = document.createElement('a');
      link.download = `template.xlsx`;
      link.href = url;
      link.click();
    });
  }

  toggleEdit() {
    this.editMode = !this.editMode;
    this.prepareTableData();
    this.editColumn = '';
    this.editRow = -1;
  }

hasEditAccess(): boolean {
  // find out if project is a scoping project and not submitted
  if (!this.isInitialUser) {
    return true;
  }
  return !this.checklistService.getIsSubmittedProject();
}

  private getDefaultColumns() {
    return [
      new ProjectChecklistColumn({
        uuid: uuid(),
        name: this.translationService.get('PROJECT.PROJECT_INFO.CHECKLIST.DEFAULT_COLUMNS.SUCCESS_CRITERIA'),
        acceptedItems: [],
        cellDropdownItems: [],
        locked: false
      }),
      new ProjectChecklistColumn({
        uuid: uuid(),
        name: this.translationService.get('PROJECT.PROJECT_INFO.CHECKLIST.DEFAULT_COLUMNS.COMMENTS'),
        acceptedItems: [],
        cellDropdownItems: [],
        locked: false
      }),
      new ProjectChecklistColumn({
        uuid: uuid(),
        name: this.translationService.get('PROJECT.PROJECT_INFO.CHECKLIST.DEFAULT_COLUMNS.LEVEL'),
        acceptedItems: [],
        cellDropdownItems: [],
        locked: false
      }),
      new ProjectChecklistColumn({
        uuid: uuid(),
        name: this.translationService.get('PROJECT.PROJECT_INFO.CHECKLIST.DEFAULT_COLUMNS.STAGE'),
        acceptedItems: [],
        cellDropdownItems: [],

        locked: false
      }),
      new ProjectChecklistColumn({
        uuid: uuid(),
        name: this.translationService.get('PROJECT.PROJECT_INFO.CHECKLIST.DEFAULT_COLUMNS.COMPLEXITY'),
        acceptedItems: [],
        cellDropdownItems: [],

        locked: false
      }),
      new ProjectChecklistColumn({
        uuid: uuid(),
        name: this.translationService.get('PROJECT.PROJECT_INFO.CHECKLIST.DEFAULT_COLUMNS.ACCEPTANCE'),
        acceptedItems: [],
        cellDropdownItems: [],
        locked: false
      }),
    ];
  }
}

